/**
 * Learner Impersonation Dialog
 * Allows teachers to select and impersonate a learner
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { useImpersonation } from '../../contexts/ImpersonationContext';

interface Learner {
  id: number;
  name: string;
  student_id: string;
  grade_level: number;
  classes: Array<{
    id: number;
    name: string;
  }>;
}

interface LearnerImpersonationDialogProps {
  open: boolean;
  onClose: () => void;
}

const LearnerImpersonationDialog: React.FC<LearnerImpersonationDialogProps> = ({
  open,
  onClose,
}) => {
  const [learners, setLearners] = useState<Learner[]>([]);
  const [filteredLearners, setFilteredLearners] = useState<Learner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLearner, setSelectedLearner] = useState<Learner | null>(null);
  const [reason, setReason] = useState('');

  const { startImpersonation, loading: impersonationLoading } = useImpersonation();

  useEffect(() => {
    if (open) {
      fetchLearners();
    }
  }, [open]);

  useEffect(() => {
    // Filter learners based on search query
    if (searchQuery.trim() === '') {
      setFilteredLearners(learners);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredLearners(
        learners.filter(
          (learner) =>
            learner.name.toLowerCase().includes(query) ||
            learner.student_id.toLowerCase().includes(query) ||
            learner.classes.some((c) => c.name.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, learners]);

  const fetchLearners = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/impersonate/learners/');
      setLearners(response.data.learners || []);
      setFilteredLearners(response.data.learners || []);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to load learners';
      setError(errorMsg);
      console.error('Error fetching learners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedLearner) return;

    try {
      await startImpersonation(selectedLearner.id, reason);
      // Dialog will close automatically when page reloads
    } catch (err: any) {
      setError(err.message || 'Failed to start impersonation');
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedLearner(null);
    setReason('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>View as Learner (Impersonation)</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a learner from your classes to view the system from their perspective.
          All actions will be audited.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Search Field */}
            <TextField
              fullWidth
              label="Search Learners"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, student ID, or class..."
              sx={{ mb: 2 }}
            />

            {/* Learners List */}
            {filteredLearners.length === 0 ? (
              <Alert severity="info">
                {learners.length === 0
                  ? 'No learners found. You may not have any learners in your classes.'
                  : 'No learners match your search criteria.'}
              </Alert>
            ) : (
              <List
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {filteredLearners.map((learner) => (
                  <ListItem key={learner.id} disablePadding>
                    <ListItemButton
                      selected={selectedLearner?.id === learner.id}
                      onClick={() => setSelectedLearner(learner)}
                    >
                      <ListItemText
                        primary={`${learner.name} (${learner.student_id})`}
                        secondary={
                          <Box>
                            <Typography variant="body2" component="span">
                              Grade {learner.grade_level}
                            </Typography>
                            <Box mt={0.5}>
                              {learner.classes.map((cls) => (
                                <Chip
                                  key={cls.id}
                                  label={cls.name}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {/* Reason Field */}
            {selectedLearner && (
              <TextField
                fullWidth
                label="Reason (Optional)"
                variant="outlined"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Testing learner view, helping with submission..."
                multiline
                rows={2}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={impersonationLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleStartImpersonation}
          variant="contained"
          disabled={!selectedLearner || impersonationLoading}
        >
          {impersonationLoading ? 'Starting...' : 'Start Impersonation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LearnerImpersonationDialog;
